/** FC255 pure intake; eBay Update retains FC252 history until Change 2. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Stack,
  Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddIcon from "@mui/icons-material/Add";
import * as api from "./pureIntakeApi";
import SimpleEbayUpdate from "./SimpleEbayUpdate";
import { activeRows, costReady, exactCard, finishes, newOperation, readPending,
  requestForCard, snapshot, stagedRows, summary, validCost, wholeQty, writePending } from "./pureIntakeRules";

const errorText = (error) => error?.response?.data?.error || error?.message || "Request failed";
const formatMoney = (value) => `AU$${Number(value || 0).toFixed(4)}`;
const showCardName = (card) => card?.name_and_number || card?.card_name || "Unknown print";
const resultCard = (record) => <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
  {record.image_url ? <Box component="img" src={record.image_url} alt="" sx={{ width: 38, height: 52, objectFit: "contain" }} /> :
    <Box sx={{ width: 38, height: 52, bgcolor: "action.hover", display: "grid", placeItems: "center", borderRadius: 1, flexShrink: 0 }}>
      <Typography fontSize={11}>TCG</Typography></Box>}
  <Box minWidth={0}><Typography fontWeight={600} variant="body2">{showCardName(record)}</Typography>
    <Typography variant="caption" color="text.secondary" display="block">{record.set_name || record.catalog_set_name || "Set unverified"}</Typography></Box>
</Stack>;

function ExactLookup({ query, onQuery, results, busy, selected, onSelect, label = "Find exact card" }) {
  return <Stack spacing={1}>
    <TextField fullWidth size="small" label={label} value={query} onChange={e => onQuery(e.target.value)}
      placeholder="Search by card name or complete number, e.g. Ultra Ball 150/172"
      InputProps={{ endAdornment: busy ? <CircularProgress size={16} /> : <SearchIcon color="action" /> }} />
    {query.trim().length >= 2 && <Box sx={{ maxHeight: 250, overflowY: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}>
      <Stack spacing={0} divider={<Divider />}>
        {results.map(record => <Button key={record.id} color="inherit" onClick={() => onSelect(record)}
          variant={selected?.id === record.id ? "outlined" : "text"} disabled={!exactCard(record)}
          sx={{ justifyContent: "flex-start", textAlign: "left", textTransform: "none", p: 1, minHeight: 66 }}>
          {resultCard(record)}<Typography variant="caption" sx={{ ml: "auto", whiteSpace: "nowrap", pl: 1 }}>
            {exactCard(record) ? `#${record.id}` : "Incomplete identity"}</Typography>
        </Button>)}
        {!busy && !results.length && <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
          No verified results yet. Enter at least two characters; never select a guessed set.</Typography>}
      </Stack>
    </Box>}
  </Stack>;
}

export default function PlayableCardIntake() {
  const [tab, setTab] = useState(0);
  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [newBatchOpen, setNewBatchOpen] = useState(false);
  const [newBatch, setNewBatch] = useState({ name: "", source: "", operation: newOperation("fc255-batch") });
  const [costDraft, setCostDraft] = useState("");
  const [costReason, setCostReason] = useState("");
  const [costApproved, setCostApproved] = useState(false);
  const [zeroApproved, setZeroApproved] = useState(false);
  const [costAudit, setCostAudit] = useState(null);
  const [costPending, setCostPending] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [recent, setRecent] = useState([]);
  const [showRecent, setShowRecent] = useState(false);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [chosen, setChosen] = useState(null);
  const [finish, setFinish] = useState("Normal");
  const [qty, setQty] = useState("1");
  const [photoRef, setPhotoRef] = useState("");
  const [pending, setPending] = useState(readPending);
  const [dialog, setDialog] = useState(null);
  const [correction, setCorrection] = useState(null);
  const [dialogQuery, setDialogQuery] = useState("");
  const [dialogResults, setDialogResults] = useState([]);
  const [dialogBusy, setDialogBusy] = useState(false);
  const [audit, setAudit] = useState(null);
  const [group, setGroup] = useState(false);
  const [finaliseUncertain, setFinaliseUncertain] = useState(false);
  const flight = useRef(false);
  const currentBatch = useMemo(() => batches.find(b => String(b.id) === String(batchId)), [batches, batchId]);
  const open = currentBatch?.status === "open";
  const totals = useMemo(() => summary(rows), [rows]);
  const editableRows = useMemo(() => stagedRows(rows), [rows]);
  const costOk = costReady(currentBatch);
  const canEditCost = Boolean(currentBatch) && currentBatch.status !== "cancelled";

  const refreshBatches = useCallback(async (preferred) => {
    const response = await api.listBatches();
    const list = Array.isArray(response.data) ? response.data : [];
    setBatches(list);
    setBatchId(previous => {
      const requested = preferred || previous;
      return list.some(b => String(b.id) === String(requested)) ? requested :
        (list.find(b => b.status === "open")?.id || list[0]?.id || "");
    });
    return list;
  }, []);
  const refreshRows = useCallback(async (id) => {
    if (!id) return;
    const response = await api.readRows(id);
    setRows(previous => String(id) === String(batchId) ? response.data?.rows || [] : previous);
    return response.data?.rows || [];
  }, [batchId]);

  useEffect(() => {
    let live = true;
    api.listBatches().then(response => {
      if (!live) return;
      const list = Array.isArray(response.data) ? response.data : [];
      setBatches(list);
      const unresolvedId = readPending()?.batchId;
      setBatchId(list.find(b => String(b.id) === String(unresolvedId))?.id ||
        list.find(b => b.status === "open")?.id || list[0]?.id || "");
    }).catch(e => { if (live) setError(errorText(e)); }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);
  useEffect(() => {
    setRows([]); setError(""); setNotice(""); setCostAudit(null);
    if (!batchId) return undefined;
    let live = true;
    api.readRows(batchId).then(r => { if (live) setRows(r.data?.rows || []); })
      .catch(e => { if (live) setError(errorText(e)); });
    return () => { live = false; };
  }, [batchId]);
  useEffect(() => {
    setCostDraft(currentBatch?.batch_unit_cost == null ? "" : Number(currentBatch.batch_unit_cost).toFixed(4));
    setCostReason(""); setCostApproved(false); setZeroApproved(false); setCostPending(null);
  }, [currentBatch?.id, currentBatch?.batch_cost_version]);
  useEffect(() => {
    if (query.trim().length < 2 || chosen || pending) { setResults([]); setLookupBusy(false); return undefined; }
    let live = true;
    const timer = setTimeout(() => {
      setLookupBusy(true);
      api.findCards(query.trim()).then(r => { if (live) setResults(r.data?.data || []); })
        .catch(e => { if (live) setError(`Catalogue lookup: ${errorText(e)}`); })
        .finally(() => { if (live) setLookupBusy(false); });
    }, 250);
    return () => { live = false; clearTimeout(timer); };
  }, [query, chosen, pending]);
  useEffect(() => {
    if (!dialog || dialog.mode !== "correct" || dialogQuery.trim().length < 2) {
      setDialogResults([]); setDialogBusy(false); return undefined;
    }
    let live = true;
    const timer = setTimeout(() => {
      setDialogBusy(true);
      api.findCards(dialogQuery.trim()).then(r => { if (live) setDialogResults(r.data?.data || []); })
        .catch(e => { if (live) setError(`Correction lookup: ${errorText(e)}`); })
        .finally(() => { if (live) setDialogBusy(false); });
    }, 250);
    return () => { live = false; clearTimeout(timer); };
  }, [dialog, dialogQuery]);

  const perform = async (fn, success) => {
    if (flight.current) return false;
    flight.current = true; setBusy(true); setError(""); setNotice("");
    try { await fn(); if (success) setNotice(success); return true; }
    catch (e) { setError(errorText(e)); return false; }
    finally { flight.current = false; setBusy(false); }
  };
  const createBatchNow = async () => {
    if (!newBatch.name.trim() || !newBatch.operation || busy) return;
    const { name, source, operation } = newBatch;
    const ok = await perform(async () => {
      const response = await api.createBatch({ batch_name: name.trim(), source_label: source.trim(), operation_id: operation });
      const id = response.data?.batch?.id;
      if (!id) throw new Error("Batch not confirmed. Refresh batches before retrying the same operation.");
      await refreshBatches(id);
    }, "New batch created. Review and save this batch’s own unit cost before adding cards.");
    if (ok) { setNewBatchOpen(false); setNewBatch({ name: "", source: "", operation: newOperation("fc255-batch") }); }
  };
  const saveCost = async () => {
    if (!currentBatch || !validCost(costDraft) || !costApproved || costReason.trim().length < 3 ||
        (Number(costDraft) === 0 && !zeroApproved)) return;
    const payload = costPending || { unit_cost: costDraft.trim(), reason: costReason.trim(),
      expected_version: Number(currentBatch.batch_cost_version || 0), operation_id: newOperation("fc255-cost"),
      confirm_zero: Number(costDraft) === 0 };
    setCostPending(payload);
    const ok = await perform(async () => {
      const result = await api.setBatchCost(batchId, payload);
      if (!result.data?.success) throw new Error("Cost confirmation was not returned. Retry same operation.");
      setCostPending(null);
      try { await refreshBatches(batchId); await refreshRows(batchId); }
      catch (_) { /* Cost was confirmed; refresh failures must not trigger a new cost write. */ }
    }, "Reviewed batch cost saved with an audit trail and any required Rule A revaluation.");
    if (ok) setCostPending(null);
  };
  const addCard = async (event) => {
    event.preventDefault();
    if (!open || !costOk || !exactCard(chosen) || !wholeQty(qty) || !finishes.includes(finish) || busy || pending) return;
    const operation = { batchId, payload: requestForCard({ card: chosen, finish, quantity: qty,
      cost: currentBatch.batch_unit_cost, reference: photoRef, requestId: newOperation("fc255-row") }) };
    writePending(operation); setPending(operation);
    await submitPending(operation);
  };
  const submitPending = async (operation) => {
    if (flight.current || !operation) return;
    flight.current = true; setBusy(true); setError(""); setNotice("");
    try {
      const response = await api.stageCard(operation.batchId, operation.payload);
      if (!response.data?.success || response.data?.request_id !== operation.payload.request_id || !response.data?.row?.id)
        throw new Error("No confirmation of exact saved row. Retry using the same operation ID.");
      writePending(null); setPending(null); setChosen(null); setQuery(""); setResults([]);
      setRecent(previous => [chosen, ...previous.filter(card => card?.id !== chosen?.id)].filter(Boolean).slice(0, 8));
      setFinish("Normal"); setQty("1");
      setNotice(`Staged ${operation.payload.quantity} × ${operation.payload.name_and_number}. No legacy physical inventory or eBay stock changed.`);
      await refreshRows(operation.batchId);
      await refreshBatches(operation.batchId);
    } catch (e) {
      const status = e?.response?.status;
      if (status >= 400 && status < 500) { writePending(null); setPending(null); }
      setError(`${errorText(e)}${!status || status >= 500 ? ". Save outcome uncertain: retry this SAME operation ID after reviewing batch." : ""}`);
    } finally { flight.current = false; setBusy(false); }
  };
  const openCorrection = (row) => {
    setDialog({ mode: "correct", row }); setDialogQuery(""); setDialogResults([]);
    setCorrection({ catalog_card_id: row.catalog_card_id, name_and_number: row.card_name,
      set_name: row.catalog_set_name, card_variant: row.card_variant, quantity: String(row.quantity),
      reason: "Corrected photo intake entry" }); setError("");
  };
  const saveCorrection = async () => {
    if (!correction || !wholeQty(correction.quantity) || !finishes.includes(correction.card_variant)) return;
    const original = dialog.row;
    const payload = { expected_revision: Number(original.revision || 1), reason: correction.reason.trim(),
      catalog_card_id: Number(correction.catalog_card_id), name_and_number: correction.name_and_number,
      set_name: correction.set_name, card_variant: correction.card_variant,
      quantity: wholeQty(correction.quantity), unit_cost: String(original.unit_cost),
      source_type: original.source_type || "bulk_lot", source_reference: original.source_reference,
      card_condition: "Near Mint" };
    if (payload.reason.length < 3) return;
    const ok = await perform(async () => {
      await api.correctRow(batchId, original.id, payload);
      await refreshRows(batchId); await refreshBatches(batchId);
    }, `Row #${original.id} corrected and audited. No legacy physical inventory or eBay stock changed.`);
    if (ok) { setDialog(null); setCorrection(null); }
  };
  const removeRow = async () => {
    if (!dialog?.row) return;
    const row = dialog.row;
    const ok = await perform(async () => {
      await api.voidRow(batchId, row.id, { expected_revision: Number(row.revision || 1), reason: "Removed incorrect staged photo entry" });
      await refreshRows(batchId); await refreshBatches(batchId);
    }, `Staged row #${row.id} removed with audit; no stock was received.`);
    if (ok) setDialog(null);
  };
  const clearStaged = async () => {
    if (!open || !editableRows.length || busy || pending) return;
    const rowsToVoid = [...editableRows];
    const ok = await perform(async () => {
      let completed = 0;
      try {
        for (const row of rowsToVoid) {
          await api.voidRow(batchId, row.id, {
            expected_revision: Number(row.revision || 1), reason: "Operator cleared staged batch",
          });
          completed += 1;
        }
      } catch (e) {
        throw new Error(`Removed ${completed} of ${rowsToVoid.length} staged rows. ${errorText(e)}. Refresh to reconcile; do not blindly retry.`);
      } finally {
        try { await refreshRows(batchId); await refreshBatches(batchId); }
        catch (_) { /* Mutation results remain authoritative. */ }
      }
    }, `Cleared ${rowsToVoid.length} staged rows with individual audit history. No physical stock removed.`);
    if (ok) setDialog(null);
  };
  const openAudit = async (row) => {
    setDialog({ mode: "audit", row }); setAudit(null); setError("");
    try { const response = await api.readAudit(batchId, row.id); setAudit(response.data?.events || []); }
    catch (e) { setError(errorText(e)); }
  };
  const finalise = async () => {
    if (!open || !costOk || !editableRows.length || pending || busy || finaliseUncertain) return;
    const expected = snapshot(rows);
    const ok = await perform(async () => {
      const response = await api.finaliseBatch(batchId, { confirm_physical_receipt: true,
        expected_snapshot: expected, confirm_zero_unit_costs: Number(currentBatch.batch_unit_cost) === 0 });
      if (!response.data?.success) throw new Error("Pure receipt not confirmed. Refresh batch before retry.");
      await refreshRows(batchId); await refreshBatches(batchId);
    }, `Pure receipt finalised once for batch #${batchId}. No eBay updates were made.`);
    if (ok) setDialog(null);
    else { setFinaliseUncertain(true); setDialog(null); }
  };
  const visibleRows = useMemo(() => group ? [...activeRows(rows)].sort((a, b) =>
    String(a.catalog_card_id).localeCompare(String(b.catalog_card_id)) ||
    String(a.card_variant).localeCompare(String(b.card_variant))) : activeRows(rows), [rows, group]);
  const canStage = open && costOk && !busy && !pending && !costPending;
  const canFinalise = canStage && editableRows.length > 0 && !finaliseUncertain &&
    editableRows.every(row => row.catalog_card_id && row.catalog_set_name &&
      ["near mint", "nm"].includes(String(row.card_condition || "").toLowerCase()) &&
      validCost(row.unit_cost) && Number(row.unit_cost) === Number(currentBatch.batch_unit_cost));

  return <Stack spacing={1.5} sx={{ pb: 3, px: { xs: 0, md: 1 } }}>
    <Box><Typography variant="h5" fontWeight={700}>Playable Card Intake</Typography>
      <Typography variant="body2" color="text.secondary">Two steps: receive your cards in Quick Intake, then update eBay after verified listing review. Receiving never changes eBay stock.</Typography></Box>
    <Tabs value={tab} onChange={(_, value) => setTab(value)} aria-label="Playable Card Intake workflow">
      <Tab label="1. Quick Intake" /><Tab label="2. eBay Update" />
    </Tabs>
    {tab === 1 ? <>
      <SimpleEbayUpdate />
    </> : <>
      <Alert severity="info">Pure intake records the exact card, quantity and COGS only. Finalised cards are available in eBay Update; no SKU or physical allocation is required here.</Alert>
      <Alert severity="info">Pure intake: Codex enters exact cards through Computer Use. No SKU or allocation required. Correct card, set, finish or quantity before finalising. Condition is <strong>Near Mint</strong>; non-NM cards need separate review.</Alert>
      {loading && <CircularProgress size={22} />}
      {error && <Alert severity="error" role="alert">{error}</Alert>}
      {notice && <Alert severity="success" role="status">{notice}</Alert>}
      {pending && <Alert severity="warning" action={<Button size="small" variant="outlined" disabled={busy} onClick={() => submitPending(pending)}>Retry same save</Button>}>
        Unconfirmed pure-intake card save: batch #{pending.batchId}, {pending.payload?.name_and_number}, qty {pending.payload?.quantity}. Check saved rows and retry the same request ID; do not re-add as a new card.</Alert>}
      {finaliseUncertain && <Alert severity="warning" action={<Button size="small" onClick={async () => {
        try { await refreshRows(batchId); await refreshBatches(batchId); setFinaliseUncertain(false); }
        catch (e) { setError(errorText(e)); }
      }}>Refresh batch</Button>}>Finalisation response uncertain. Refresh and verify the current batch status before trying again.</Alert>}
      <Card variant="outlined"><CardContent sx={{ pb: "16px !important" }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={1.5} alignItems={{ md: "center" }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} flex={1}>
            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 310 } }}>
              <InputLabel id="fc255-batch-label">Current batch</InputLabel>
              <Select labelId="fc255-batch-label" label="Current batch" value={batchId} disabled={busy || Boolean(pending)}
                onChange={e => { setBatchId(e.target.value); setGroup(false); setFinaliseUncertain(false); }}>
                {batches.map(b => <MenuItem key={b.id} value={b.id}>#{b.id} {b.batch_name} ({b.status})</MenuItem>)}
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={() => { setNewBatchOpen(true); setError(""); }} disabled={busy || Boolean(pending)}>New Batch</Button>
            <Button variant="text" size="small" disabled={busy || Boolean(pending)} onClick={async () => {
              try { await refreshBatches(batchId); await refreshRows(batchId); }
              catch (e) { setError(errorText(e)); }
            }}>Refresh</Button>
            {currentBatch && <Chip label={open ? "Open for staging" : "Finalised — read-only"} color={open ? "info" : "success"} size="small" />}
          </Stack>
          <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`Staged: ${totals.stagedCount}`} color="warning" />
            <Chip size="small" label={`Received: ${totals.receivedCount}`} color="success" />
            <Chip size="small" label={`Distinct prints: ${totals.distinct}`} />
            <Chip size="small" label={`Rows: ${totals.entries}`} />
          </Stack>
        </Stack>
      </CardContent></Card>
      {currentBatch && <Grid container spacing={1.5}>
        <Grid item xs={12} lg={6}><Card variant="outlined" sx={{ height: "100%" }}><CardContent>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>Add cards to batch</Typography>
          {!costOk && <Alert severity="warning" sx={{ mb: 1 }}>Set and review this batch’s own unit cost before staging cards.</Alert>}
          <Tabs value={showRecent ? 1 : 0} onChange={(_, value) => setShowRecent(value === 1)} sx={{ minHeight: 35, mb: 1 }}>
            <Tab label="Search Catalogue" sx={{ minHeight: 35 }} /><Tab label="Recent Cards" sx={{ minHeight: 35 }} />
          </Tabs>
          {showRecent ? <Stack spacing={0.5} sx={{ maxHeight: 235, overflowY: "auto" }}>
            {recent.map(card => <Button key={card.id} variant="outlined" onClick={() => { setChosen(card); setShowRecent(false); setQuery(""); }}
              sx={{ justifyContent: "flex-start", textTransform: "none" }}>{resultCard(card)}</Button>)}
            {!recent.length && <Typography variant="body2" color="text.secondary">Recently added cards will appear here during this session.</Typography>}
          </Stack> : <ExactLookup query={query} onQuery={value => { setQuery(value); setChosen(null); }}
            results={results} busy={lookupBusy} selected={chosen} onSelect={card => { setChosen(card); setQuery(""); setResults([]); }} />}
          {chosen && <Box sx={{ mt: 1, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">Selected exact print — verify set and number</Typography>
            {resultCard(chosen)}
            <Stack component="form" onSubmit={addCard} direction={{ xs: "column", sm: "row" }} gap={1} sx={{ mt: 1.5 }} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 145 }}><InputLabel>Finish</InputLabel>
                <Select label="Finish" value={finish} onChange={e => setFinish(e.target.value)} disabled={!canStage}>
                  {finishes.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                </Select></FormControl>
              <TextField label="Quantity" size="small" type="number" value={qty} sx={{ width: 108 }}
                onChange={e => setQty(e.target.value)} inputProps={{ min: 1, step: 1 }} disabled={!canStage} />
              <Button type="submit" startIcon={<AddIcon />} fullWidth variant="contained"
                disabled={!canStage || !wholeQty(qty)} sx={{ minHeight: 40 }}>Add to Batch</Button>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <Chip label="Near Mint (automatic)" size="small" color="info" />
              <Button size="small" onClick={() => { setChosen(null); setQuery(""); }}>Change card</Button>

            </Stack>
          </Box>}
          <TextField size="small" fullWidth sx={{ mt: 1.5 }} label="Photo / lot reference (optional)" value={photoRef}
            onChange={e => setPhotoRef(e.target.value)} inputProps={{ maxLength: 255 }} helperText="Preserved on each receipt as provenance." />
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight={700} mb={1}>Batch unit COGS (AUD per card)</Typography>
          <Alert severity={costOk ? "success" : "warning"} sx={{ mb: 1 }}>
            {costOk ? `${formatMoney(currentBatch.batch_unit_cost)} per card · reviewed version ${currentBatch.batch_cost_version}` :
              "No cost confirmed for this batch. Saving cards is blocked."}
          </Alert>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField size="small" label="Cost / card" value={costDraft} onChange={e => {
              setCostDraft(e.target.value); setCostApproved(false); setZeroApproved(false); setCostPending(null);
            }} sx={{ minWidth: 145 }} inputProps={{ inputMode: "decimal" }} disabled={busy || !canEditCost} />
            <TextField size="small" label="Cost reason" value={costReason} fullWidth onChange={e => { setCostReason(e.target.value); setCostApproved(false); }}
              inputProps={{ maxLength: 500 }} disabled={busy || !canEditCost} />
          </Stack>
          <FormControlLabel control={<Checkbox size="small" checked={costApproved} disabled={busy || !canEditCost || !validCost(costDraft)}
            onChange={e => setCostApproved(e.target.checked)} />} label={<Typography variant="body2">I reviewed this batch cost and approve applying it to all cards in this batch.</Typography>} />
          {Number(costDraft) === 0 && <FormControlLabel control={<Checkbox size="small" checked={zeroApproved} onChange={e => setZeroApproved(e.target.checked)} />}
            label={<Typography variant="body2">Explicitly approve zero unit cost</Typography>} />}
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Button variant="outlined" size="small" onClick={saveCost} disabled={busy || !canEditCost || !validCost(costDraft) ||
              !costApproved || costReason.trim().length < 3 || (Number(costDraft) === 0 && !zeroApproved) ||
              (costOk && Number(costDraft) === Number(currentBatch.batch_unit_cost) && !costPending)}>
              {costPending ? "Retry same cost update" : costOk ? "Save cost change" : "Confirm batch cost"}</Button>
            <Button size="small" onClick={async () => { try { const r = await api.getBatchCostAudit(batchId);
              setCostAudit(r.data?.events || []); } catch (e) { setError(errorText(e)); } }}>Cost History</Button>
          </Stack>
          {costAudit && <Box sx={{ mt: 1 }}>{costAudit.length ? costAudit.map(row => <Typography key={row.id} variant="caption" display="block">
            v{row.batch_version}: {row.old_cost || "unset"} → {row.new_cost}, {row.reason}
          </Typography>) : <Typography variant="caption">No cost changes recorded.</Typography>}</Box>}
        </CardContent></Card></Grid>
        <Grid item xs={12} lg={6}><Card variant="outlined" sx={{ height: "100%" }}><CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
            <Typography variant="subtitle1" fontWeight={700}>Batch contents — {totals.entries} entries</Typography>
            <Tooltip title="Sort identical entries together without merging or deleting their receipt IDs"><Button variant="outlined" size="small" onClick={() => setGroup(old => !old)}>
              {group ? "Original order" : "Group identical cards"}</Button></Tooltip>
          </Stack>
          <TableContainer sx={{ maxHeight: 490 }}><Table size="small" stickyHeader aria-label="FC252 staged card entries">
            <TableHead><TableRow><TableCell>Card / Number</TableCell><TableCell>Set</TableCell><TableCell>Finish</TableCell>
              <TableCell align="right">Qty</TableCell><TableCell>Action</TableCell></TableRow></TableHead>
            <TableBody>
              {visibleRows.map(row => <TableRow key={row.id} sx={row.inventory_applied_at ? { bgcolor: "action.hover" } : {}}>
                <TableCell><Stack direction="row" alignItems="center" spacing={0.7}>
                  {row.catalog_image_url && <Box component="img" src={row.catalog_image_url} alt="" sx={{ height: 35, width: 27, objectFit: "contain" }} />}
                  <Typography variant="body2" fontWeight={600}>{row.card_name}</Typography></Stack>
                  <Typography variant="caption" color="text.secondary">#{row.id} · {row.inventory_applied_at ? "Received" : "Staged"}</Typography></TableCell>
                <TableCell><Typography variant="body2">{row.catalog_set_name || "UNKNOWN — review"}</Typography></TableCell>
                <TableCell><Typography variant="body2">{row.card_variant}</Typography></TableCell>
                <TableCell align="right">{row.quantity}</TableCell>
                <TableCell><Stack direction="row" gap={0.5} flexWrap="wrap">
                  <Button size="small" startIcon={<EditOutlinedIcon fontSize="small" />} disabled={!open || busy || Boolean(row.inventory_applied_at) || Boolean(pending)}
                    onClick={() => openCorrection(row)}>Correct</Button>
                  <Button size="small" color="error" disabled={!open || busy || Boolean(row.inventory_applied_at) || Boolean(pending)}
                    onClick={() => setDialog({ mode: "remove", row })}>Remove</Button>
                  <Button size="small" onClick={() => openAudit(row)}>History</Button>
                </Stack></TableCell>
              </TableRow>)}
              {!visibleRows.length && <TableRow><TableCell colSpan={5}>No cards in this batch. Add a verified catalogue print to begin.</TableCell></TableRow>}
            </TableBody>
          </Table></TableContainer>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} mt={2} gap={1}>
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2">{totals.stagedCount} staged · {totals.receivedCount} received · {totals.removed} removed</Typography>
              <Button color="error" variant="outlined" size="small" disabled={!open || !editableRows.length || busy || Boolean(pending)}
                onClick={() => setDialog({ mode: "clear" })}>Clear staged batch</Button>
            </Stack>
            <Button variant="contained" color="success" disabled={!canFinalise} onClick={() => setDialog({ mode: "finalise" })}>
              Finalise Intake Batch</Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Finalising receives physical stock exactly once. It does not update eBay.</Typography>
        </CardContent></Card></Grid>
      </Grid>}
      {!currentBatch && !loading && <Alert severity="warning">Create your first FC252 batch to begin.</Alert>}
      <Dialog open={newBatchOpen} onClose={() => !busy && setNewBatchOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Playable Card Intake batch</DialogTitle>
        <DialogContent><Stack spacing={2} mt={1}>
          <TextField autoFocus label="Batch name" fullWidth value={newBatch.name} onChange={e => setNewBatch(old => ({ ...old, name: e.target.value }))} />
          <TextField label="Source / lot (optional)" fullWidth value={newBatch.source} onChange={e => setNewBatch(old => ({ ...old, source: e.target.value }))} />
          <Alert severity="info">Each new batch needs its own reviewed unit COGS. Previous batch costs are never copied.</Alert>
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => setNewBatchOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={createBatchNow} disabled={busy || !newBatch.name.trim()}>Create Batch</Button></DialogActions>
      </Dialog>
      <Dialog open={Boolean(dialog)} onClose={() => !busy && setDialog(null)} fullWidth maxWidth="md">
        <DialogTitle>{dialog?.mode === "correct" ? "Correct Card" : dialog?.mode === "remove" ? "Remove staged card" :
          dialog?.mode === "audit" ? "Receipt correction history" :
          "Finalise pure intake"}</DialogTitle>
        <DialogContent><Stack spacing={1.5} sx={{ pt: 1 }}>
          {dialog?.mode === "correct" && correction && <>
            <Grid container spacing={2}><Grid item xs={12} md={4}>
              <Typography variant="subtitle2">Current card (this row)</Typography>
              <Typography variant="body2">{dialog.row.card_name}</Typography>
              <Typography variant="body2">{dialog.row.catalog_set_name}</Typography>
              <Chip size="small" label={dialog.row.card_variant} /> <Chip size="small" label={`Qty ${dialog.row.quantity}`} />
            </Grid><Grid item xs={12} md={8}>
              <Typography variant="subtitle2" mb={1}>Find replacement exact card (only if identity is wrong)</Typography>
              <ExactLookup query={dialogQuery} onQuery={setDialogQuery} results={dialogResults} busy={dialogBusy}
                selected={{ id: correction.catalog_card_id }} onSelect={card => {
                  setCorrection(old => ({ ...old, catalog_card_id: card.id, name_and_number: card.name_and_number, set_name: card.set_name }));
                  setDialogQuery(""); setDialogResults([]);
                }} label="Search replacement card / set" />
            </Grid></Grid>
            <Divider />
            <Typography variant="subtitle2">Confirm corrected card: {correction.name_and_number} — {correction.set_name}</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
              <FormControl size="small" fullWidth><InputLabel>Finish</InputLabel><Select label="Finish" value={correction.card_variant}
                onChange={e => setCorrection(old => ({ ...old, card_variant: e.target.value }))}>
                  {finishes.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}</Select></FormControl>
              <TextField label="Quantity" size="small" fullWidth type="number" value={correction.quantity}
                inputProps={{ min: 1, step: 1 }} onChange={e => setCorrection(old => ({ ...old, quantity: e.target.value }))} />
              <TextField label="Condition" size="small" fullWidth value="Near Mint (NM)" disabled />
            </Stack>
            <TextField fullWidth size="small" label="Audit reason" value={correction.reason} inputProps={{ maxLength: 500 }}
              onChange={e => setCorrection(old => ({ ...old, reason: e.target.value }))} />
            <Alert severity="info">Saves the existing staged row with an audit event. Quantity-only correction preserves the exact card. No eBay or physical stock change.</Alert>
          </>}
          {dialog?.mode === "clear" && <Alert severity="warning">Remove all {editableRows.length} staged rows from batch #{batchId}? Each row is individually soft-voided with an audit history. Finalised receipts are not touched. If interrupted, the remaining rows will stay staged for review.</Alert>}
          {dialog?.mode === "remove" && <Typography>Remove staged row #{dialog.row.id}: {dialog.row.card_name} × {dialog.row.quantity}? The row remains in audit history and cannot be replayed as new stock.</Typography>}
          {dialog?.mode === "audit" && (audit ? audit.length ? audit.map(event => <Alert key={event.id} severity="info">
            {event.action} · {event.reason}<Typography variant="caption" display="block">Before: {event.before_json}</Typography>
            <Typography variant="caption" display="block">After: {event.after_json}</Typography></Alert>) :
            <Typography>No corrections recorded.</Typography> : <CircularProgress size={20} />)}
          {dialog?.mode === "finalise" && <>
            <Typography>Finalise <strong>{totals.stagedCount} received cards</strong> across {editableRows.length} staged rows in batch #{batchId}?</Typography>
            <Typography>Reviewed batch cost: <strong>{formatMoney(currentBatch?.batch_unit_cost)} per card.</strong></Typography>
            <Alert severity="warning">This records received cards without changing legacy on-hand inventory, SKU allocations, listings or eBay. The FC255 eBay handoff is part of Change 2.</Alert>
          </>}
        </Stack></DialogContent>
        <DialogActions><Button disabled={busy} onClick={() => { setDialog(null); setAudit(null); }}>Cancel / Close</Button>
          {dialog?.mode === "correct" && <Button variant="contained" disabled={busy || !wholeQty(correction?.quantity) ||
            !correction?.catalog_card_id || !correction?.set_name || !finishes.includes(correction?.card_variant) ||
            correction?.reason?.trim().length < 3} onClick={saveCorrection}>Save Correction</Button>}
          {dialog?.mode === "remove" && <Button variant="contained" color="error" disabled={busy} onClick={removeRow}>Remove Staged Row</Button>}
          {dialog?.mode === "clear" && <Button variant="contained" color="error" disabled={busy || !editableRows.length} onClick={clearStaged}>Confirm Clear Batch</Button>}
          {dialog?.mode === "finalise" && <Button variant="contained" color="success" disabled={busy || !canFinalise} onClick={finalise}>Confirm Pure Receipt</Button>}
        </DialogActions>
      </Dialog>
    </>}
  </Stack>;
}
